import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { RequestService } from '../service/request.service';
import { GetterService } from '../service/getter-service.service';
import { ApiService } from '../service/api.service';
import { RecipeOptionModalService } from '../recipe-option-modal/recipe-option-modal.service';
import { DesignService } from '../design/design.service';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { Recipe } from 'src/Model/Product-Interfaces';
import { brandFlavor } from 'src/app/brand/brand-context';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  /**
   * TemplateData.NewDesign (DesignService): the redesigned look (field and
   * results on src/_ui-tokens.scss, app-new-recipe-card results — inside
   * app-new-header) vs the legacy one (Bootstrap input-group, app-recipe —
   * inside app-header). Each header is shown only in its own mode, so the
   * switch decides both.
   */
  newRecipeCards = false;
  ui = false;
  /** a legacy page that embeds the search itself (store) keeps the legacy look in both modes */
  @Input() legacyLook = false;
  designSub: Subscription;
  /** hosted inside a header's full-screen mobile search sheet, which already
   *  paints the scrim and handles dismissal: no second scrim, results in flow */
  @Input() embedded = false;
  /** embedded only: the trailing × asked to close the host's search sheet
   *  (it clears the text first while there is any — see clearOrClose) */
  @Output() close = new EventEmitter<void>();
  @ViewChild('searchField') searchField: ElementRef<HTMLInputElement>;
  @ViewChild('results') resultsEl: ElementRef<HTMLElement>;

  /** the results panel (and, on desktop, its scrim) is showing — the same
   *  test the template uses; the new header holds its bar while this is true */
  get panelOpen(): boolean {
    return !!(
      (this.recipeData?.length || this.productData?.length || this.notfound) &&
      this.focus &&
      this.searchField?.nativeElement.value
    );
  }
  notfound: any = 0;
  products: any;
  isLoading: boolean;
  recipeData: Recipe[] = [];
  productData: any = [];

  focus: boolean;
  searchResult: string;
  searchResSub: Subscription;
  webCusSub: Subscription;
  addedToCartSub: Subscription;
  routerSub: Subscription;
  vehiclesBrand: boolean;
  brandFlavor: string;
  partsBrand: any;

  constructor(
    private _requestService: RequestService,
    private _local: GetterService,
    private apiService: ApiService,
    private optionModal: RecipeOptionModalService,
    private router: Router,
    private design: DesignService
  ) {
    this.brandFlavor = brandFlavor();
  }

  ngOnInit() {
    // close the results panel once a result has taken the customer somewhere
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.focus = false;
    });

    this.designSub = this.design.isNew$.subscribe((isNew) => {
      this.ui = this.newRecipeCards = isNew && !this.legacyLook;
    });

    this.webCusSub = this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) {
        return;
      }

      this.vehiclesBrand = res.TemplateData?.VehiclesBrand;
      this.partsBrand = res.TemplateData?.PartsBrand;
    });
    this.addedToCartSub = this.optionModal.addedToCart$.subscribe(
      (data) => {
        this.productData.forEach((prod) => {
          if (prod.ProductId == data.ProductId) {
            prod.ProductQuantity = data.ProductQuantity;
          }
        });
      }
    );
  }

  ngAfterViewInit(): void {
    // phone sheet: the customer tapped "search", so they can type at once.
    // Synchronous on purpose — iOS only raises the keyboard for a focus()
    // that happens inside the tap's own task; a setTimeout would lose it.
    if (this.embedded) this.searchField?.nativeElement.focus();
  }

  ngAfterViewChecked(): void {
    this.fitPanel();
  }

  @HostListener('window:resize')
  onResize() {
    this.fitPanel();
  }

  /** Esc: close the results panel first; with nothing open, an embedded
   *  search asks its host sheet to close */
  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.panelOpen) {
      this.focus = false;
      // drop DOM focus too, so the next click on the field fires focus
      this.searchField?.nativeElement.blur();
      return;
    }
    if (this.embedded) this.close.emit();
  }

  /**
   * The results panel must end inside the viewport: the page behind it is
   * inert while it is open (scrim cancels scrolling, the new header holds
   * its bar), so anything past the bottom edge would be unreachable. Its
   * top depends on the host (header bar, banner above it, phone sheet), so
   * the height is measured rather than a fixed max-height.
   */
  private fitPanel() {
    const el = this.resultsEl?.nativeElement;
    if (!el) return;
    const room = window.innerHeight - el.getBoundingClientRect().top - 16;
    const px = Math.max(220, Math.floor(room)) + 'px';
    if (el.style.maxHeight !== px) el.style.maxHeight = px;
  }

  ngOnDestroy(): void {
    this.designSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.webCusSub.unsubscribe();
    this.addedToCartSub.unsubscribe();
  }

  onsubmit() {}

  searchProducts(inputEvent: Event) {
    if (this.searchResSub) {
      this.searchResSub.unsubscribe();
    }
    const value = (inputEvent.target as HTMLInputElement).value;
    if (value.length < 4) {
      this.recipeData = [];
      this.productData = [];
      this.isLoading = false;
      this.searchResult = null;
      return;
    }

    this.isLoading = true;
    let type: string = this._requestService.deliveryType$.value;

    if (type) {
      if (type == 'Home') {
        type = 'Delivery';
      } else if (type == 'Store') {
        type = 'Pickup';
      }
    }

    let body = {
      BrandFlavor: this.brandFlavor,
      SearchKey: value,
      Offset: 0,
      Limit: 40,
      OrderOption: type,
      OnlineOrderingEnabled: true,
    };

    let link = '';
    if (this.vehiclesBrand) {
      link = 'api/Product/searchVehicle';
    } else if (this.partsBrand) {
      link = 'api/Product/SearchDetailProduct';
    } else {
      link = 'api/Product/search';
    }
    this.searchResSub = this._requestService
      .postRequest(link, JSON.stringify(body))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.Info.Status == 200) {
            this.searchResult = null;
            this.notfound = 0;

            this.recipeData = res['Data']?.RecipeData
              ? res['Data']?.RecipeData
              : [];

            this.productData = res['Data'].ProductData
              ? res['Data'].ProductData
              : [];

            const filteredRecipes = this.recipeData.filter(
              (recipe) => recipe.ForCollection || recipe.ForDelivery
            );

            this.recipeData = filteredRecipes;

            let cart = JSON.parse(this._local.get('cartProductItems'));
            cart?.forEach((product) => {
              this.productData.forEach((item) => {
                if (item.ProductId == product.ProductId) {
                  item.ProductQuantity = product.ProductQuantity;
                }
              });
            });
          } else {
            this.notfound = 1;
            this.recipeData = [];
            this.productData = [];
            this.searchResult = 'No item found';
          }
        },
        error: () => {
          this.notfound = 1;
          this.isLoading = false;
          this.recipeData = [];
          this.productData = [];
          this.searchResult = 'Something went wrong';
        },
      });
  }
  /** trailing × (embedded): clear the text while there is some — the
   *  customer keeps the sheet to retype — and close the sheet once empty */
  clearOrClose() {
    const input = this.searchField?.nativeElement;
    if (input?.value) {
      input.value = '';
      // through the input event so ngModel (#searchInput) and searchProducts'
      // own reset both see the empty value
      input.dispatchEvent(new Event('input', { bubbles: true }));
      this.notfound = 0;
      input.focus();
      return;
    }
    this.close.emit();
  }

  onBlur() {
    this.focus = false;
  }
  onFocus() {
    this.focus = true;
  }
}
